import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Code, Bot, Cpu, Calculator, Languages, ArrowRight, Star, Users, BookOpen,
  GraduationCap, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Sparkles, Target, Monitor, UserCheck,
  Zap, Award, Puzzle, Globe, Phone, Mail, CheckCircle, ArrowUpRight, Menu, X, Shield, AlertTriangle,
  Quote, MessageCircle, MapPin, Clock, Heart, Headphones
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════
//  COUNTRY CONFIG — single source of truth
// ═══════════════════════════════════════
const COUNTRY_OPTIONS = [
  { value: 'india', label: 'India', flag: '🇮🇳' },
  { value: 'usa', label: 'USA', flag: '🇺🇸' },
  { value: 'canada', label: 'Canada', flag: '🇨🇦' },
  { value: 'uk', label: 'UK', flag: '🇬🇧' },
  { value: 'australia', label: 'Australia', flag: '🇦🇺' },
  { value: 'singapore', label: 'Singapore', flag: '🇸🇬' },
  { value: 'other', label: 'Other', flag: '🌍' },
];

const REGION_CONFIG = {
  india: {
    primaryCTA: 'Book a Demo',
    secondaryCTA: 'Contact Us',
    showFreeTrial: false,
    currency: 'INR',
    showContactForm: true,
    heroSubtext: 'Customizable 1:1 and Group classes to address varying needs',
    heroSubtext2: 'Start with a guided consultation',
    heroBadge: 'Trusted by 500+ Parents across India',
    bottomCTA: 'Book a Demo Session',
    bottomSubmit: 'Book My Demo',
    demoTitle: 'Book a Demo Session',
    demoDesc: 'Fill in the details and we\'ll set up a personalized demo session',
    highlights: [
      { icon: Clock, text: 'Flexible Timings' },
      { icon: Heart, text: 'Affordable Live Classes' },
      { icon: Headphones, text: 'Parent Support' },
    ],
  },
  international: {
    primaryCTA: 'Book Free Trial Class',
    secondaryCTA: 'Explore Programs',
    showFreeTrial: true,
    currency: 'LOCAL',
    showContactForm: false,
    heroSubtext: 'Give your child a head start in tech with a FREE Trial Class',
    heroSubtext2: 'Experience our 1:1 live online coding classes risk-free',
    heroBadge: 'Rated 4.8/5 by 500+ Parents',
    bottomCTA: 'Book a FREE Trial',
    bottomSubmit: 'Book My FREE Trial',
    demoTitle: 'Book a FREE Trial Class',
    demoDesc: 'Fill in the details and we\'ll set up a personalized demo session',
    highlights: [
      { icon: Monitor, text: '1:1 Online Classes' },
      { icon: Users, text: 'Ages 6 to 16' },
      { icon: Shield, text: 'Expert-Led Curriculum' },
    ],
  },
};

const getRegion = (country) => country === 'india' ? 'india' : 'international';

const detectCountryFromTimezone = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz.startsWith('Asia/Kolkata') || tz.startsWith('Asia/Calcutta')) return 'india';
    if (tz.startsWith('America/New_York') || tz.startsWith('America/Chicago') || tz.startsWith('America/Denver') || tz.startsWith('America/Los_Angeles') || tz.startsWith('US/')) return 'usa';
    if (tz.startsWith('America/Toronto') || tz.startsWith('Canada/')) return 'canada';
    if (tz.startsWith('Europe/London')) return 'uk';
    if (tz.startsWith('Australia/')) return 'australia';
    if (tz.startsWith('Asia/Singapore')) return 'singapore';
  } catch {}
  return null;
};

const NAV_ITEMS = [
  { id: 'programs', label: 'Programs' },
  { id: 'difference', label: 'The Difference' },
  { id: 'about', label: 'About Us' },
  { id: 'courses', label: 'Courses' },
  { id: 'faq', label: 'FAQ' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ students: 0, teachers: 0, courses: 0, total_revenue_inr: 0 });
  const [demoOpen, setDemoOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', age: '', grade: '', country: '', email: '', phone: '', timezone: '', course_interest: '', preferred_date: '', preferred_time: '', message: '' });
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '', preferred_date: '', preferred_time: '', comments: '' });
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeNav, setActiveNav] = useState('');

  // Country selection
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const saved = localStorage.getItem('codeitt_country');
    if (saved) return saved;
    return detectCountryFromTimezone() || '';
  });
  const [showCountryPicker, setShowCountryPicker] = useState(!selectedCountry);

  const region = getRegion(selectedCountry);
  const config = REGION_CONFIG[region] || REGION_CONFIG.international;

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    localStorage.setItem('codeitt_country', country);
    setShowCountryPicker(false);
  };

  const sectionRefs = {
    programs: useRef(null),
    testimonials: useRef(null),
    difference: useRef(null),
    about: useRef(null),
    faq: useRef(null),
  };
  const scrollContainerRef = useRef(null);

  const HERO_CAPTIONS = [
    { text: 'The Skills They Learn Today', highlight: 'Shape the Leaders of Tomorrow' },
    { text: 'Empower Your Child With a', highlight: 'Head Start in Tech' },
    { text: '1:1 Expert Mentoring That', highlight: 'Builds Confident Creators' },
    { text: 'While Others Watch Screens,', highlight: 'Your Child Builds the Future' },
    { text: 'From Curious Kids to', highlight: 'Confident Coders — We Bridge the Gap' },
  ];
  const [captionIdx, setCaptionIdx] = useState(() => Math.floor(Date.now() / 3600000) % HERO_CAPTIONS.length);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCaptionIdx(prev => (prev + 1) % HERO_CAPTIONS.length);
        setFadeIn(true);
      }, 400);
    }, 3600000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/public/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      (entries) => { for (const entry of entries) { if (entry.isIntersecting) setActiveNav(entry.target.id); } },
      { root: container, rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );
    Object.values(sectionRefs).forEach(ref => { if (ref.current) observer.observe(ref.current); });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = useCallback((id) => {
    if (id === 'courses') { navigate('/programs'); return; }
    const el = sectionRefs[id]?.current;
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setMobileMenu(false); }
  }, [navigate]);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.email || !demoForm.phone) { toast.error('Fill required fields'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/demo-bookings`, demoForm);
      toast.success('Demo booked! We\'ll reach out soon.');
      setDemoOpen(false);
      setDemoForm({ name: '', age: '', grade: '', country: '', email: '', phone: '', timezone: '', course_interest: '', preferred_date: '', preferred_time: '', message: '' });
    } catch { toast.error('Submission failed'); }
    setSubmitting(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.phone) { toast.error('Fill required fields'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/demo-bookings`, {
        name: contactForm.name, phone: contactForm.phone, email: contactForm.email,
        preferred_date: contactForm.preferred_date, preferred_time: contactForm.preferred_time,
        message: contactForm.comments, country: 'India', source: 'contact_us'
      });
      toast.success('Thank you! We\'ll contact you shortly.');
      setContactOpen(false);
      setContactForm({ name: '', phone: '', email: '', preferred_date: '', preferred_time: '', comments: '' });
    } catch { toast.error('Submission failed'); }
    setSubmitting(false);
  };

  const handlePrimaryCTA = () => {
    const message = encodeURIComponent("Hi! I would like to book a free trial class at CodeITT Academy.");
    window.open(`https://wa.me/917204638532?text=${message}`, '_blank', 'noopener,noreferrer');
  };

  const handleSecondaryCTA = () => {
    if (region === 'india') {
      setContactOpen(true);
    } else {
      navigate('/programs');
    }
  };

  const programs = [
    { level: 'Tech Sprout', age: '6-12', tagline: 'Spark curiosity and creativity with hands-on tech and playful coding', color: '#2563eb', link: '/programs?category=coding&program=tech-sprout', features: ['Explore coding through fun puzzles & storytelling', 'Create interactive animations & simple digital games', 'Learn basic logic building & problem solving', 'Build foundations in Scratch & Code.org'], icon: Sparkles },
    { level: 'Tech Titan', age: '13-16', tagline: 'Transform ideas into real-world tech with advanced programming', color: '#7c3aed', link: '/programs?category=coding&program=tech-titan', features: ['Master Python, Java, C++ & Web Development', 'Build real apps, games & interactive websites', 'Learn data structures & algorithmic thinking', 'Create AI-powered projects & chatbots'], icon: Code },
    { level: 'AI & Beyond', age: 'All Ages', tagline: 'Master AI, ML and cutting-edge tech to lead in a tech-first world', color: '#ea580c', link: '/programs?category=ai&program=ai-beyond', features: ['Prompt Engineering & AI Tools mastery', 'Train real Machine Learning models', 'Build standout projects for portfolios', 'Prepare for future tech careers'], icon: Bot },
  ];

  const faqs = [
    { q: 'What is the duration and frequency of classes?', a: 'Our live 1:1 online classes typically run for 1 hour and are held 2-4 times weekly. We offer flexible scheduling options to accommodate your child\'s schedule.' },
    { q: 'What programs are available?', a: 'We provide Coding (Scratch, Python, Java, HTML & CSS, App Development, Game Development), GenAI, Robotics, Mathematics, and Language Learning programs for kids aged 6-16.' },
    { q: 'Do you provide assessments?', a: 'Yes, we conduct periodic assessments and generate detailed progress reports shared with parents.' },
    { q: 'What sets CodeITT apart?', a: 'Our 1:1 live classes with dedicated teachers, mastery-based curriculum, real-time feedback, detailed progress tracking, and hands-on project-based learning make us unique.' },
    { q: 'How do I get started?', a: region === 'india' ? 'Simply book a Demo session! Select a course, choose your preferred time slot, and experience our teaching firsthand.' : 'Simply book a FREE trial class! Select a course, choose your preferred time slot, and experience our teaching firsthand.' },
    { q: 'What is the cancellation policy?', a: 'You can cancel your subscription anytime with 1 month\'s notice. We believe in earning your trust every month through quality education.' },
  ];

  const studentTestimonials = [
    { name: 'Abhimanyu G.', topic: 'Scratch & Python', text: 'I used to just play games but now I make them! My teacher makes coding super fun and I already built 3 projects on my own.' },
    { name: 'Prithavi K.', topic: 'Code.org', text: 'The classes are so cool! I love solving puzzles and my teacher always helps me when I get stuck. I even taught my friends some coding!' },
    { name: 'Ariel M.', topic: 'Scratch', text: 'I made an animated story about space for my school project and everyone loved it. CodeITT helped me think like a real programmer.' },
    { name: 'Dhanvith R.', topic: 'Python & AI', text: 'Learning AI felt impossible before but my teacher explained everything step by step. Now I can build chatbots and it feels amazing!' },
  ];

  const parentTestimonials = [
    { parent: 'Mrs. Goyal', child: 'Abhimanyu', text: 'The 1:1 attention my son gets is incredible. His logical thinking and problem-solving have improved across all subjects, not just coding.' },
    { parent: 'Mr. Kumar', child: 'Prithavi', text: 'The teachers are patient and skilled. My daughter looks forward to every class. The progress reports help us see exactly how she\'s improving.' },
    { parent: 'Mrs. Martinez', child: 'Ariel', text: 'We tried group classes elsewhere and our child felt lost. CodeITT\'s personalized approach made all the difference. Highly recommend!' },
  ];

  const aboutCategories = [
    { id: 'problem', label: 'The Problem', color: '#DC2626', icon: AlertTriangle,
      content: ["Today's children are growing up in a technology-driven world, but most schools still focus on rote learning rather than real-world skills.", "Students often:"],
      bullets: ["Consume technology but don't understand how it works", "Lack problem-solving and logical thinking skills", "Are unprepared for AI, robotics, and future tech careers"],
      footer: "Parents want their children to be future-ready, not just exam-ready." },
    { id: 'solution', label: 'The Solution', color: '#7C3AED', icon: Zap,
      content: ["CodeITT Academy bridges this gap by introducing students to coding, robotics, computational thinking, and innovation skills at an early age.", "We transform students from technology users into technology creators.", "At CodeITT, learning is:"],
      bullets: ["Practical", "Project-based", "Future-focused", "Fun and engaging"] },
    { id: 'how', label: 'How We Teach', color: '#0891B2', icon: BookOpen,
      content: ["Unlike traditional classes, CodeITT focuses on learning by building.", "Our approach includes:"],
      bullets: ["Concept-driven lessons", "Hands-on projects", "Game-based learning", "Coding competitions", "Mentor-led guidance"],
      footer: "Students don't just listen \u2014 they create." },
    { id: 'who', label: 'Who It Is For', color: '#059669', icon: Users,
      content: ["CodeITT programs are designed for students aged 7 to 16 years.", "Suitable for:"],
      bullets: ["Beginners with no coding experience", "Curious learners who love technology", "Students preparing for future tech careers"] },
    { id: 'why', label: 'Why Parents Choose', color: '#D97706', icon: Award,
      content: [""],
      bullets: ["Small batch learning", "Hands-on practical curriculum", "Global coding skills exposure", "Confidence building through projects", "Competition & innovation opportunities"],
      footer: "Most importantly, children develop problem-solving skills that benefit every subject." },
  ];

  const active = aboutCategories.find(c => c.id === activeCategory);

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ fontFamily: "'Nunito', sans-serif" }} data-testid="landing-page">

      {/* ===== COUNTRY SELECTOR BANNER ===== */}
      {showCountryPicker && (
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-3 px-4 sm:px-8 flex-shrink-0 z-[60] relative" data-testid="country-picker-banner">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-bold">Select your country to see relevant pricing & offers</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {COUNTRY_OPTIONS.map(c => (
                <button key={c.value} onClick={() => handleCountrySelect(c.value)}
                  className="px-3 py-1.5 text-xs font-bold rounded-full border border-white/20 hover:bg-white hover:text-slate-900 transition-all duration-200 flex items-center gap-1.5"
                  data-testid={`country-${c.value}`}>
                  <span>{c.flag}</span> {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== NAVBAR ===== */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-slate-200 flex-shrink-0 z-50 sticky top-0 shadow-sm" data-testid="navbar">
        <div className="w-full px-4 sm:px-8 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-4 cursor-pointer flex-shrink-0" onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="/assets/logo.png" alt="CodeITT" className="h-16 sm:h-20 w-auto object-contain rounded-2xl" data-testid="codeitt-logo" />
          </div>

          <div className="hidden lg:flex items-center gap-2" data-testid="nav-pills">
            {NAV_ITEMS.map(item => (
              <button key={item.id} onClick={() => scrollToSection(item.id)}
                className={`px-5 py-2 rounded-full text-sm font-extrabold uppercase tracking-wide transition-all duration-300 border-2 ${
                  activeNav === item.id ? 'bg-[#3B82F6]/10 text-[#1E40AF] border-[#3B82F6]/40 shadow-sm' : 'text-[#334155] border-transparent hover:text-[#1E40AF] hover:bg-[#F0F7FF] hover:border-[#3B82F6]/20'
                }`} data-testid={`nav-${item.id}`}>
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Country change button (small) */}
            {selectedCountry && (
              <button onClick={() => setShowCountryPicker(true)} className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-all border border-slate-200" data-testid="change-country-btn">
                <MapPin className="w-3 h-3" /> {COUNTRY_OPTIONS.find(c => c.value === selectedCountry)?.flag} {COUNTRY_OPTIONS.find(c => c.value === selectedCountry)?.label}
              </button>
            )}
            <Button onClick={() => navigate('/login')} className="hidden sm:inline-flex bg-[#3B82F6] hover:bg-[#2563EB] text-white font-extrabold rounded-full px-7 h-10 text-sm uppercase tracking-wide shadow-lg shadow-blue-500/25" data-testid="login-btn">Login</Button>
            <button onClick={() => setMobileMenu(!mobileMenu)} className="lg:hidden p-1.5 hover:bg-slate-100 rounded-lg">
              {mobileMenu ? <X className="w-6 h-6 text-[#0F172A]" /> : <Menu className="w-6 h-6 text-[#0F172A]" />}
            </button>
          </div>
        </div>

        {mobileMenu && (
          <div className="lg:hidden bg-white border-t border-slate-200 px-4 py-3 space-y-1">
            {selectedCountry && (
              <button onClick={() => { setShowCountryPicker(true); setMobileMenu(false); }} className="block w-full text-left px-4 py-2.5 text-sm font-bold text-slate-500 rounded-full hover:bg-slate-100">
                <MapPin className="w-3 h-3 inline mr-1" /> Change Country ({COUNTRY_OPTIONS.find(c => c.value === selectedCountry)?.label})
              </button>
            )}
            {NAV_ITEMS.map(item => (
              <button key={item.id} onClick={() => scrollToSection(item.id)}
                className={`block w-full text-left px-4 py-2.5 text-sm font-bold uppercase rounded-full transition-all ${
                  activeNav === item.id ? 'bg-[#3B82F6]/10 text-[#1E40AF]' : 'text-[#334155] hover:bg-[#F0F7FF] hover:text-[#1E40AF]'
                }`}>
                {item.label}
              </button>
            ))}
            <button onClick={() => { navigate('/login'); setMobileMenu(false); }}
              className="block w-full text-center mt-2 px-3 py-2.5 text-sm font-extrabold rounded-full bg-[#3B82F6] text-white uppercase" data-testid="mobile-login-btn">
              Login
            </button>
          </div>
        )}
      </nav>

      {/* ===== SCROLLABLE CONTENT ===== */}
      <main ref={scrollContainerRef} className="flex-1 overflow-y-auto scroll-smooth" data-testid="scroll-content">

        {/* ── HERO SECTION ── */}
        <section className="relative min-h-[85vh] flex items-center overflow-hidden" data-testid="hero-section" style={{ background: 'linear-gradient(135deg, #0B1437 0%, #1E1B4B 35%, #3B0F6B 70%, #1E3A8A 100%)' }}>
          <div className="absolute inset-0 overflow-hidden">
            <img src="/hero-bg.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-right" style={{ objectPosition: '75% center' }} />
            {/* Left-side darken to keep CTA text legible; right-side stays vibrant */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,20,55,0.85) 0%, rgba(11,20,55,0.55) 40%, rgba(11,20,55,0.15) 75%, rgba(11,20,55,0) 100%)' }} />
            {/* Subtle vignette top + bottom */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,20,55,0.25) 0%, transparent 25%, transparent 75%, rgba(11,20,55,0.4) 100%)' }} />
            {/* Mobile darkener */}
            <div className="absolute inset-0 sm:hidden" style={{ background: 'rgba(11,20,55,0.55)' }} />
            {/* Floating particles (CSS-only) */}
            <div className="hero-particles absolute inset-0 pointer-events-none" aria-hidden="true">
              <span className="hp-dot" style={{left:'12%', top:'22%', animationDelay:'0s'}} />
              <span className="hp-dot" style={{left:'28%', top:'68%', animationDelay:'1.4s'}} />
              <span className="hp-dot" style={{left:'40%', top:'30%', animationDelay:'2.8s'}} />
              <span className="hp-dot" style={{left:'55%', top:'78%', animationDelay:'0.7s'}} />
              <span className="hp-dot" style={{left:'70%', top:'18%', animationDelay:'2.1s'}} />
              <span className="hp-dot" style={{left:'82%', top:'62%', animationDelay:'3.5s'}} />
            </div>
          </div>
          <div className="relative z-10 w-full px-6 sm:px-10 lg:px-16 py-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm text-cyan-300 rounded-full text-xs sm:text-sm font-extrabold mb-5 border border-cyan-400/20" data-testid="hero-badge">
              <Star className="w-4 h-4 fill-cyan-300 text-cyan-300" /> {config.heroBadge}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-4 tracking-tight max-w-4xl uppercase" data-testid="hero-title"
              style={{ transition: 'opacity 0.4s ease', opacity: fadeIn ? 1 : 0 }}>
              {HERO_CAPTIONS[captionIdx].text}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-purple-300">{HERO_CAPTIONS[captionIdx].highlight}</span>
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-blue-100 leading-relaxed mb-2 max-w-2xl font-bold" data-testid="hero-subtext">
              {config.heroSubtext}
            </p>
            <p className="text-xs sm:text-sm text-blue-200/80 mb-6 max-w-2xl font-semibold" data-testid="hero-subtext2">
              {config.heroSubtext2}
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-7">
              {config.highlights.map((badge, i) => {
                const Icon = badge.icon;
                return <div key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border bg-cyan-500/15 text-cyan-300 border-cyan-400/30 text-xs sm:text-sm font-extrabold"><Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> {badge.text}</div>;
              })}
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <button onClick={handlePrimaryCTA}
                className="group relative px-7 sm:px-9 py-3.5 sm:py-4 rounded-full bg-gradient-to-br from-cyan-300 via-cyan-400 to-purple-500 text-white font-extrabold text-sm sm:text-base uppercase tracking-wide flex items-center gap-2 transition-all duration-300 hover:scale-[1.04] hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                style={{
                  boxShadow: '0 10px 25px -5px rgba(168, 85, 247, 0.5), 0 8px 12px -6px rgba(34, 211, 238, 0.45), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 6px rgba(124, 58, 237, 0.4)'
                }}
                data-testid="hero-primary-cta">
                <span className="relative z-10 flex items-center gap-2">{config.primaryCTA} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
              </button>
              <button onClick={handleSecondaryCTA}
                className="relative px-7 sm:px-9 py-3.5 sm:py-4 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 text-white font-extrabold text-sm sm:text-base uppercase tracking-wide border border-white/40 transition-all duration-300 hover:scale-[1.04] hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0 flex items-center gap-2"
                style={{
                  boxShadow: '0 8px 20px -6px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -2px 6px rgba(0,0,0,0.15)'
                }}
                data-testid="hero-secondary-cta">
                {config.secondaryCTA}
              </button>
            </div>
            {/* Stats row */}
            <div className="flex items-center gap-8 sm:gap-14 lg:gap-20 mt-8">
              {[
                { n: '10+', l: 'Subjects', icon: BookOpen, color: 'text-cyan-300', bg: 'bg-cyan-500/15' },
                { n: '15+', l: 'Countries', icon: Globe, color: 'text-purple-300', bg: 'bg-purple-500/15' },
                { n: '50+', l: 'Certifications', icon: Award, color: 'text-amber-300', bg: 'bg-amber-500/15' },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="text-center" data-testid={`stat-${s.l.toLowerCase()}`}>
                    <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-xl ${s.bg} flex items-center justify-center mx-auto mb-2`}><Icon className={`w-5 h-5 sm:w-7 sm:h-7 ${s.color}`} /></div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-white">{s.n}</div>
                    <div className="text-[10px] sm:text-xs font-extrabold text-blue-200 uppercase tracking-wider">{s.l}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── PROGRAMS SECTION ── */}
        <section id="programs" ref={sectionRefs.programs} className="scroll-mt-20 py-16 sm:py-20 px-6 sm:px-10 lg:px-16 bg-[#F8FAFC]" data-testid="programs-section">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F172A] mb-3 uppercase tracking-wide">From Curious Minds to <span className="text-[#1E40AF]">Tech Leaders</span></h2>
              <p className="text-base sm:text-lg text-[#334155] font-bold">Empowering kids to transform from beginners to innovators</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {programs.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl transition-shadow duration-300" data-testid={`program-card-${i}`}>
                    <div className="p-6 pb-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: p.color }}><Icon className="w-6 h-6 text-white" /></div>
                        <div><h3 className="text-lg font-extrabold text-[#0F172A]">{p.level}</h3><p className="text-sm font-bold" style={{ color: p.color }}>Ages {p.age}</p></div>
                      </div>
                      <p className="text-sm text-[#334155] font-semibold mb-4 leading-relaxed">{p.tagline}</p>
                      <div className="space-y-2.5">
                        {p.features.map((f, fi) => (
                          <div key={fi} className="flex items-start gap-2"><Star className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: p.color }} /><span className="text-sm text-[#334155] font-bold">{f}</span></div>
                        ))}
                      </div>
                    </div>
                    <div className="px-6 pb-6 pt-2">
                      <Button onClick={() => navigate(p.link)} className="w-full rounded-full h-11 font-extrabold text-white text-sm uppercase tracking-wide shadow-md hover:shadow-lg transition-shadow" style={{ backgroundColor: p.color }}>Explore Curriculum <ArrowRight className="w-4 h-4 ml-1" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS SECTION ── */}
        <section id="testimonials" ref={sectionRefs.testimonials} className="scroll-mt-20 py-16 sm:py-20 px-6 sm:px-10 lg:px-16 bg-white border-y border-slate-100" data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F172A] mb-3">What Our Students & Parents <span className="text-[#1E40AF]">Say</span></h2>
              <p className="text-base text-[#334155] font-semibold">Real stories from our learning community</p>
            </div>
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-lg bg-[#1E40AF]/10 flex items-center justify-center"><GraduationCap className="w-4 h-4 text-[#1E40AF]" /></div>
                <h3 className="text-lg font-extrabold text-[#0F172A]">Student Voices</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {studentTestimonials.map((t, i) => (
                  <div key={i} className="bg-[#F8FAFC] rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow relative" data-testid={`student-testimonial-${i}`}>
                    <Quote className="w-8 h-8 text-[#1E40AF]/15 absolute top-4 right-4" />
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E40AF] to-[#7C3AED] flex items-center justify-center text-white font-extrabold text-sm">{t.name[0]}</div>
                      <div><p className="font-extrabold text-[#0F172A] text-sm">{t.name}</p><p className="text-xs font-bold text-[#1E40AF]">{t.topic}</p></div>
                    </div>
                    <p className="text-sm text-[#334155] leading-relaxed font-medium">"{t.text}"</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-lg bg-[#059669]/10 flex items-center justify-center"><Users className="w-4 h-4 text-[#059669]" /></div>
                <h3 className="text-lg font-extrabold text-[#0F172A]">Parent Feedback</h3>
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                {parentTestimonials.map((t, i) => (
                  <div key={i} className="bg-[#F8FAFC] rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow relative" data-testid={`parent-testimonial-${i}`}>
                    <Quote className="w-8 h-8 text-[#059669]/15 absolute top-4 right-4" />
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#059669] to-[#0891B2] flex items-center justify-center text-white font-extrabold text-sm">{t.parent[0]}</div>
                      <div><p className="font-extrabold text-[#0F172A] text-sm">{t.parent}</p><p className="text-xs font-bold text-[#059669]">Parent of {t.child}</p></div>
                    </div>
                    <p className="text-sm text-[#334155] leading-relaxed font-medium">"{t.text}"</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── DIFFERENCE SECTION ── */}
        <section id="difference" ref={sectionRefs.difference} className="scroll-mt-20 py-16 sm:py-20 px-6 sm:px-10 lg:px-16 bg-white border-y border-slate-100" data-testid="difference-section">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F172A] mb-3 uppercase tracking-wide">The CodeITT <span className="text-[#1E40AF]">Difference</span></h2>
              <p className="text-base sm:text-lg text-[#334155] font-bold">See why families choose personalized 1:1 mentoring over one-size-fits-all alternatives</p>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
              <div className="grid grid-cols-3 border-b border-slate-200">
                <div className="p-4 bg-slate-50" />
                <div className="p-4 text-center border-l border-slate-200 bg-slate-50"><p className="text-sm font-extrabold text-[#64748B] uppercase tracking-wide">Traditional Tutoring</p></div>
                <div className="p-4 text-center border-l border-slate-200 bg-[#EFF6FF]">
                  <div className="flex items-center justify-center gap-2"><img src="/assets/logo.png" alt="CodeITT" className="h-7 w-auto object-contain" /><p className="text-sm font-extrabold text-[#1E40AF] uppercase tracking-wide">CodeITT</p></div>
                </div>
              </div>
              {[
                { feature: 'Class Format', trad: 'Group classes, shared attention', codeitt: 'Dedicated 1:1 live sessions' },
                { feature: 'Curriculum', trad: 'Generic, fixed-pace syllabus', codeitt: 'Adaptive, mastery-based learning' },
                { feature: 'Teacher Quality', trad: 'Variable experience levels', codeitt: 'Vetted experts with tech backgrounds' },
                { feature: 'Progress Tracking', trad: 'Occasional, informal updates', codeitt: 'Detailed reports shared with parents' },
                { feature: 'Projects & Practice', trad: 'Limited homework assignments', codeitt: 'Real-world projects & 1000+ exercises' },
                { feature: 'Schedule', trad: 'Fixed batch timings', codeitt: 'Flexible, fits your child\'s routine' },
                { feature: 'Support', trad: 'Teacher during class only', codeitt: 'Dedicated success manager + teacher' },
              ].map((row, i) => (
                <div key={i} className={`grid grid-cols-3 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} ${i < 6 ? 'border-b border-slate-100' : ''}`}>
                  <div className="p-3.5 flex items-center"><span className="text-sm sm:text-base font-extrabold text-[#0F172A]">{row.feature}</span></div>
                  <div className="p-3.5 flex items-center justify-center border-l border-slate-100 text-center"><div className="flex items-center gap-1.5"><X className="w-4 h-4 text-red-400 flex-shrink-0" /><span className="text-xs sm:text-sm text-[#64748B] font-bold">{row.trad}</span></div></div>
                  <div className="p-3.5 flex items-center justify-center border-l border-slate-100 bg-[#EFF6FF]/50 text-center"><div className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#1E40AF] flex-shrink-0" /><span className="text-xs sm:text-sm font-bold text-[#0F172A]">{row.codeitt}</span></div></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── ABOUT US SECTION ── */}
        <section id="about" ref={sectionRefs.about} className="scroll-mt-20 py-16 sm:py-20 px-6 sm:px-10 lg:px-16 bg-[#F8FAFC]" data-testid="about-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F172A] mb-2 uppercase tracking-wide">Why Choose <span className="text-[#1E40AF]">CodeITT</span></h2>
              <p className="text-sm sm:text-base text-[#334155] mt-1 font-bold">Click on any option to explore</p>
            </div>
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="flex flex-col gap-3 w-full lg:w-72 flex-shrink-0">
                {aboutCategories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = activeCategory === cat.id;
                  return (
                    <button key={cat.id} onClick={() => setActiveCategory(isActive ? null : cat.id)}
                      className={`w-full flex items-center gap-3 px-5 py-4 rounded-xl border-2 transition-all duration-200 text-left ${isActive ? 'border-white shadow-lg text-white' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md hover:translate-x-1'}`}
                      style={isActive ? { backgroundColor: cat.color } : {}} data-testid={`category-${cat.id}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : cat.color + '15' }}>
                        <Icon className="w-5 h-5" style={{ color: isActive ? '#fff' : cat.color }} />
                      </div>
                      <span className="text-sm font-bold" style={{ color: isActive ? '#fff' : '#0F172A' }}>{cat.label}</span>
                      <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${isActive ? 'rotate-90 text-white' : 'text-[#334155]'}`} />
                    </button>
                  );
                })}
                <button onClick={handlePrimaryCTA} className="w-full mt-2 py-4 rounded-xl bg-gradient-to-r from-[#1E40AF] to-[#7C3AED] text-white font-extrabold text-sm shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all border-2 border-white/30" data-testid="join-community-btn">
                  {region === 'india' ? 'Book a Demo Now' : 'Join the Community Now'}
                </button>
              </div>
              <div className="flex-1 min-h-[400px]">
                {active ? (
                  <div className="rounded-2xl border-2 p-6 sm:p-8 bg-white shadow-sm transition-all duration-300" style={{ borderColor: active.color + '30' }} data-testid="category-content">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: active.color }}>
                        {(() => { const Icon = active.icon; return <Icon className="w-5 h-5 text-white" />; })()}
                      </div>
                      <h3 className="text-xl sm:text-2xl font-extrabold" style={{ color: active.color }}>{active.label}</h3>
                    </div>
                    {active.content.filter(Boolean).map((para, pi) => (<p key={pi} className="text-sm sm:text-base text-[#334155] leading-relaxed mb-3">{para}</p>))}
                    {active.bullets && (<ul className="space-y-2 mb-3">{active.bullets.map((b, bi) => (<li key={bi} className="flex items-start gap-2.5"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: active.color }} /><span className="text-sm sm:text-base text-[#0F172A] font-medium">{b}</span></li>))}</ul>)}
                    {active.footer && <p className="text-sm sm:text-base font-bold text-[#0F172A] mt-3">{active.footer}</p>}
                  </div>
                ) : (
                  <div className="rounded-2xl border-2 border-slate-200 p-8 bg-white shadow-sm h-full flex flex-col items-center justify-center text-center" data-testid="category-placeholder">
                    <div className="w-16 h-16 rounded-full bg-[#1E40AF]/10 flex items-center justify-center mb-4"><Zap className="w-8 h-8 text-[#1E40AF]" /></div>
                    <p className="text-lg font-bold text-[#0F172A] mb-1">Explore What Makes Us Different</p>
                    <p className="text-sm text-[#334155]">Click any option on the left to learn more</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ SECTION ── */}
        <section id="faq" ref={sectionRefs.faq} className="scroll-mt-20 py-16 sm:py-20 px-6 sm:px-10 lg:px-16 bg-[#F8FAFC]" data-testid="faq-section">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F172A] mb-3">Frequently Asked <span className="text-[#1E40AF]">Questions</span></h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid={`faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors">
                    <span className="font-bold text-[#0F172A] text-base">{faq.q}</span>
                    {openFaq === i ? <ChevronUp className="w-5 h-5 text-[#334155] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#334155] flex-shrink-0" />}
                  </button>
                  {openFaq === i && <div className="px-5 pb-4 text-base text-[#334155] leading-relaxed">{faq.a}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── BOTTOM CTA SECTION ── */}
        <section className="py-16 sm:py-20 px-6 sm:px-10 lg:px-16 bg-gradient-to-r from-[#1E40AF] to-[#7C3AED]" data-testid="cta-section">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-4">Ready to Unlock Your Child's Potential?</h3>
            <p className="text-blue-100 mb-6 text-base sm:text-lg max-w-md mx-auto font-semibold">
              {region === 'india' ? 'Join thousands of parents across India who trust CodeITT Academy' : 'Join thousands of parents who trust CodeITT Academy'}
            </p>
            <Button size="lg" onClick={handlePrimaryCTA} className="bg-white text-[#1E40AF] hover:bg-blue-50 rounded-full px-8 h-12 font-extrabold text-base shadow-xl hover:shadow-2xl transition-shadow" data-testid="cta-bottom-book">
              {config.bottomCTA} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="bg-[#F1F5F9] text-[#0F172A] px-4 sm:px-6 lg:px-8 py-5 border-t border-slate-200" data-testid="footer">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <img src="/assets/logo.png" alt="CodeITT" className="h-12 w-auto rounded-xl" />
                <span className="text-base font-extrabold">Code<span className="text-[#1E40AF]">ITT</span></span>
              </div>
              <span className="text-xs text-[#64748B] font-bold">&copy; 2026 All Rights Reserved</span>
            </div>
            <div className="flex items-center gap-5 text-xs text-[#64748B] font-bold flex-wrap justify-center">
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> enquiries@codeittacademy.com</span>
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> +91 7204638532</span>
              <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> www.codeittacademy.com</span>
              <span className="flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> 4.8 rated</span>
            </div>
          </div>
        </footer>
      </main>

      {/* ===== DEMO DIALOG ===== */}
      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 p-0 shadow-2xl shadow-blue-950/50 [&>button]:text-[#94A3B8] [&>button]:hover:text-[#F8FAFC]" style={{ background: 'rgba(30, 58, 138, 0.92)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}>
          <div className="rounded-2xl p-6 pt-8">
            <DialogHeader>
              <DialogTitle className="text-xl font-extrabold text-[#F8FAFC]">{config.demoTitle}</DialogTitle>
              <DialogDescription className="text-[#94A3B8] font-bold">{config.demoDesc}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDemoSubmit} className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Child's Name *</Label><Input value={demoForm.name} onChange={e => setDemoForm({...demoForm, name: e.target.value})} placeholder="Name" required data-testid="demo-name" className="rounded-lg h-9 bg-white/95 border border-white/20 text-slate-900 font-bold placeholder:text-[#CBD5E1]" /></div>
                <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Age</Label><Input value={demoForm.age} onChange={e => setDemoForm({...demoForm, age: e.target.value})} placeholder="Age" data-testid="demo-age" className="rounded-lg h-9 bg-white/95 border border-white/20 text-slate-900 font-bold placeholder:text-[#CBD5E1]" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Grade</Label><Input value={demoForm.grade} onChange={e => setDemoForm({...demoForm, grade: e.target.value})} placeholder="Grade" className="rounded-lg h-9 bg-white/95 border border-white/20 text-slate-900 font-bold placeholder:text-[#CBD5E1]" /></div>
                <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Country</Label><Input value={demoForm.country} onChange={e => setDemoForm({...demoForm, country: e.target.value})} placeholder="Country" className="rounded-lg h-9 bg-white/95 border border-white/20 text-slate-900 font-bold placeholder:text-[#CBD5E1]" /></div>
              </div>
              <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Email *</Label><Input type="email" value={demoForm.email} onChange={e => setDemoForm({...demoForm, email: e.target.value})} placeholder="parent@email.com" required data-testid="demo-email" className="rounded-lg h-9 bg-white/95 border border-white/20 text-slate-900 font-bold placeholder:text-[#CBD5E1]" /></div>
              <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Phone *</Label><Input value={demoForm.phone} onChange={e => setDemoForm({...demoForm, phone: e.target.value})} placeholder="+91 98765 43210" required data-testid="demo-phone" className="rounded-lg h-9 bg-white/95 border border-white/20 text-slate-900 font-bold placeholder:text-[#CBD5E1]" /></div>
              <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Course Interest</Label>
                <Select value={demoForm.course_interest} onValueChange={v => setDemoForm({...demoForm, course_interest: v})}>
                  <SelectTrigger data-testid="demo-course-select" className="rounded-lg h-9 bg-white/95 border border-white/20 text-slate-900 font-bold"><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tech Sprout (6-12)">Tech Sprout (6-12)</SelectItem>
                    <SelectItem value="Tech Titan (13-16)">Tech Titan (13-16)</SelectItem>
                    <SelectItem value="AI">AI / ML</SelectItem>
                    <SelectItem value="Mathematics - AMC 8">Mathematics - AMC 8</SelectItem>
                    <SelectItem value="Mathematics - AMC 10">Mathematics - AMC 10</SelectItem>
                    <SelectItem value="Mathematics - AMC 12">Mathematics - AMC 12</SelectItem>
                    <SelectItem value="Mathematics - MATHCOUNTS">Mathematics - MATHCOUNTS</SelectItem>
                    <SelectItem value="Mathematics - Math Kangaroo">Mathematics - Math Kangaroo</SelectItem>
                    <SelectItem value="Mathematics - General">Mathematics - General</SelectItem>
                    <SelectItem value="IELTS">IELTS Preparation</SelectItem>
                    <SelectItem value="Robotics">Robotics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Preferred Date</Label><Input type="date" value={demoForm.preferred_date} onChange={e => setDemoForm({...demoForm, preferred_date: e.target.value})} className="rounded-lg h-9 bg-white/95 border border-white/20 text-slate-900 font-bold" /></div>
                <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Timezone</Label>
                  <Select value={demoForm.timezone} onValueChange={v => setDemoForm({...demoForm, timezone: v})}>
                    <SelectTrigger className="rounded-lg h-9 bg-white/95 border border-white/20 text-slate-900 font-bold"><SelectValue placeholder="Timezone" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Kolkata">IST (India)</SelectItem>
                      <SelectItem value="America/New_York">EST (US)</SelectItem>
                      <SelectItem value="Europe/London">GMT (UK)</SelectItem>
                      <SelectItem value="Asia/Dubai">GST (UAE)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="font-extrabold text-xs text-[#E2E8F0]">Message</Label><Textarea value={demoForm.message} onChange={e => setDemoForm({...demoForm, message: e.target.value})} placeholder="Any specific needs..." rows={2} className="rounded-lg bg-white/95 border border-white/20 text-slate-900 font-bold placeholder:text-[#CBD5E1]" /></div>
              <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white h-10 rounded-full font-extrabold text-sm uppercase tracking-wide shadow-lg shadow-cyan-500/20 border border-cyan-300/30 transition-all" data-testid="demo-submit">{submitting ? 'Submitting...' : config.bottomSubmit}</Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CONTACT US DIALOG (India only) ===== */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-lg bg-white border-slate-200 max-h-[90vh] overflow-y-auto rounded-2xl text-[#0F172A]">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-[#0F172A]">Contact Us</DialogTitle>
            <DialogDescription className="text-[#64748B] font-bold">Fill in your details and we'll get back to you shortly</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-3 mt-3">
            <div><Label className="font-extrabold text-xs text-[#334155]">Name *</Label><Input value={contactForm.name} onChange={e => setContactForm({...contactForm, name: e.target.value})} placeholder="Your name" required data-testid="contact-name" className="rounded-lg h-9 bg-slate-50 border-slate-200 text-[#0F172A] font-bold placeholder:text-slate-400" /></div>
            <div><Label className="font-extrabold text-xs text-[#334155]">Phone Number *</Label><Input value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} placeholder="+91 98765 43210" required data-testid="contact-phone" className="rounded-lg h-9 bg-slate-50 border-slate-200 text-[#0F172A] font-bold placeholder:text-slate-400" /></div>
            <div><Label className="font-extrabold text-xs text-[#334155]">Email ID</Label><Input type="email" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} placeholder="email@example.com" data-testid="contact-email" className="rounded-lg h-9 bg-slate-50 border-slate-200 text-[#0F172A] font-bold placeholder:text-slate-400" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="font-extrabold text-xs text-[#334155]">Preferred Date</Label><Input type="date" value={contactForm.preferred_date} onChange={e => setContactForm({...contactForm, preferred_date: e.target.value})} data-testid="contact-date" className="rounded-lg h-9 bg-slate-50 border-slate-200 text-[#0F172A] font-bold" /></div>
              <div><Label className="font-extrabold text-xs text-[#334155]">Preferred Time</Label><Input type="time" value={contactForm.preferred_time} onChange={e => setContactForm({...contactForm, preferred_time: e.target.value})} data-testid="contact-time" className="rounded-lg h-9 bg-slate-50 border-slate-200 text-[#0F172A] font-bold" /></div>
            </div>
            <div><Label className="font-extrabold text-xs text-[#334155]">Comments</Label><Textarea value={contactForm.comments} onChange={e => setContactForm({...contactForm, comments: e.target.value})} placeholder="Any specific requirements or questions..." rows={3} data-testid="contact-comments" className="rounded-lg bg-slate-50 border-slate-200 text-[#0F172A] font-bold placeholder:text-slate-400" /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] text-white h-10 rounded-full font-extrabold text-sm uppercase tracking-wide shadow-lg shadow-blue-500/20" data-testid="contact-submit">{submitting ? 'Submitting...' : 'Submit'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
